import { useDeviceStore } from '@/store';
import { DeviceLogger } from './logger';
import logger from '@/lib/logger';
import {
  encodeAppMessage,
  frameBytes,
  tryDecodeDevMessageWithCrcStatus,
  createAppMessage,
  hasError,
  getErrorMessage,
  isRecoverableError,
  validateAcknowledgement,
  getFirmwareVersion,
  getHardwareVersion,
  getProtocolVersion,
  Triggers,
  ElectrodeDetect,
  PowerState,
} from '@/lib/protobuf';
import type {
  ImpedanceExec,
  DevMessage,
  ImpedanceResult,
  AppMessage,
  DevMessageDecodeResult,
} from '@/lib/protobuf';
import type { DeviceError, DeviceInfo, DeviceState, MeasurementResult } from '@/types/device';
import type { MeasurementOptions, ConnectResult } from './types';
import { CRC_FALLBACK, DEVICE_STORAGE, EDIPHOR_BLE, TIMING, UNKNOWN_DEVICE_UID } from './constants';
import { createDeviceError, classifyError, DeviceErrorCode } from './errors';
import type { IDeviceService, TransferIncident } from './IDeviceService';
import { FrameAssembler } from './FrameAssembler';

/**
 * Options for BleDeviceService initialization.
 */
export interface BleDeviceServiceOptions {
  /**
   * Serial number to disambiguate multiple simultaneously-advertising EdiphorReader
   * units. When supplied, device discovery filters by the exact advertised name
   * "EdiphorReader <serial>" (matching the reference contract) instead of accepting
   * any device advertising the service. Story 2 wires this from the Connection Wizard
   * UI; the option exists here so that wiring doesn't require another service-shape
   * change.
   *
   * NOTE: `getInstance(options)` only applies `options` the first time it constructs the
   * singleton — every later call (including the module's own eager `bleDeviceService`
   * export and `DeviceServiceContext`, both of which call `getInstance()` with no
   * arguments) just returns the existing instance untouched. To apply a new `serial`
   * before reconnecting, callers must `resetInstance()` first (see its doc comment —
   * that reset is a real production mechanism here, not only a test helper).
   */
  serial?: string;

  /** Custom timeout overrides */
  timeouts?: {
    command?: number;
    measurement?: number;
  };
}

/**
 * BleDeviceService - Handles Web Bluetooth (BLE) communication with the Ediphor
 * measurement device.
 *
 * Targets the confirmed GATT contract (one service UUID; CHAR_WRITE, CHAR_READ notify,
 * CHAR_ERROR notify — see `_bmad-output/specs/spec-ble-connectivity/ble-gatt-contract.md`).
 * Reuses the existing protobuf/CRC32 framing code unchanged (`encodeAppMessage`,
 * `createAppMessage`, `tryDecodeDevMessageWithCrcStatus`) — this class only adds the BLE
 * transport plumbing: discovery/connect, MTU-derived write chunking, and notification
 * reassembly.
 *
 * Deliberately a standalone class implementing `IDeviceService` (like `MockDeviceService`),
 * not a subclass of `DeviceService` — see Design Notes in the story spec for why some
 * protocol-state-machine logic (`updateDeviceState`, `awaitMeasurementCompletion`) is
 * duplicated here rather than extracted into a shared module.
 */
export class BleDeviceService implements IDeviceService {
  private static instance: BleDeviceService | null = null;

  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private writeChar: BluetoothRemoteGATTCharacteristic | null = null;
  private readChar: BluetoothRemoteGATTCharacteristic | null = null;
  private errorChar: BluetoothRemoteGATTCharacteristic | null = null;

  /** The most recently successfully-connected `BluetoothDevice`, retained across a
   *  `gattserverdisconnected` event (which nulls `this.device` but never this field) so
   *  `tryAutoReconnect()` can retry against the same object without a new permission
   *  prompt. Set by both a fresh `connect()` and a successful `tryAutoReconnect()`,
   *  each right after their own `_initialiseOpenDevice()` call — not inside that shared
   *  helper, whose body this story leaves unchanged. Cleared only by an explicit
   *  user-initiated `disconnect()`. */
  private _lastDevice: BluetoothDevice | null = null;

  // Prevents concurrent tryAutoReconnect() calls from racing on this.device — mirrors
  // DeviceService.ts's field of the same name/pattern exactly.
  private _autoReconnectInProgress = false;

  /** Outbound write chunk size, computed from the connection's negotiated MTU at
   *  connect time (see `_computeChunkSize`). Never a hardcoded byte count. */
  private _chunkSize: number = EDIPHOR_BLE.DEFAULT_CHUNK_SIZE;

  /** Reassembles frames split/coalesced across GATT notifications (AD-4). Reset on
   *  connect and disconnect so a partial message is discarded, never decoded. */
  private _frameAssembler = new FrameAssembler((reason, droppedBytes) => {
    this.logger.warn('BLE RX', `Bad frame data discarded: ${reason} (${droppedBytes} byte(s))`);
  });

  private logger: DeviceLogger;
  private options: {
    serial?: string;
    timeouts: {
      command: number;
      measurement: number;
    };
  };

  private _lastCmdRaw: Uint8Array | null = null;
  private _rxResolvers: Array<{
    resolve: (msg: DevMessage | null) => void;
    reject: (err: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
    expectedId: number;
  }> = [];
  /** See `DeviceService`'s field of the same name — same correlation-id-to-trigger
   *  bookkeeping, duplicated here per the story's Design Notes. */
  private _pendingTriggerByCorrelationId: Map<number, Triggers> = new Map();
  private _unsolicitedHandlers: ((msg: DevMessage, raw: Uint8Array) => void)[] = [];
  private _sentHandlers: ((raw: Uint8Array, message: AppMessage) => void)[] = [];
  private _receivedHandlers: ((raw: Uint8Array, msg: DevMessage, crcValid: boolean) => void)[] = [];
  private _transferIncidentHandlers: ((incident: TransferIncident) => void)[] = [];

  /** Count of inbound frames whose CRC32 failed but were still decoded and used
   *  (CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH), for runtime diagnostics. */
  private _crcFallbackUsedCount = 0;

  /**
   * Get the singleton instance of BleDeviceService
   */
  public static getInstance(options?: BleDeviceServiceOptions): BleDeviceService {
    if (!BleDeviceService.instance) {
      BleDeviceService.instance = new BleDeviceService(options);
    }
    return BleDeviceService.instance;
  }

  /**
   * Reset the singleton instance.
   *
   * Used by tests, but also the production mechanism for applying a new
   * `BleDeviceServiceOptions` (e.g. a different `serial`) before the next connect —
   * `getInstance(options)` only honors `options` on the call that first constructs the
   * instance, so changing them requires resetting and re-constructing.
   */
  public static resetInstance(): void {
    if (BleDeviceService.instance) {
      BleDeviceService.instance.disconnect();
      BleDeviceService.instance = null;
    }
  }

  private constructor(options?: BleDeviceServiceOptions) {
    this.logger = DeviceLogger.setup();

    this.options = {
      serial: options?.serial,
      timeouts: {
        command: options?.timeouts?.command ?? TIMING.COMMAND_TIMEOUT,
        measurement: options?.timeouts?.measurement ?? TIMING.MAX_MEASUREMENT_DURATION,
      },
    };

    this.logger.info('BleDeviceService', 'BleDeviceService initialized', this.options);
  }

  /**
   * Check if WebUSB is supported in current browser.
   *
   * BleDeviceService is the BLE transport; this check exists purely so it satisfies
   * `IDeviceService` alongside `isBleSupported()` — it never affects BLE behavior.
   */
  public isWebUSBSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof (navigator as any).usb !== 'undefined' &&
      (navigator as any).usb !== null
    );
  }

  /**
   * Check if Web Bluetooth is supported in current browser.
   */
  public isBleSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.bluetooth !== 'undefined' &&
      navigator.bluetooth !== null
    );
  }

  /**
   * Compute the outbound write chunk size from the connection's negotiated MTU,
   * mirroring the reference dev-console's `min(244, max(20, mtu - 3))` formula —
   * the intent, not the literal 244 constant (Web Bluetooth's MTU visibility differs
   * from bleak's OS-level access). Falls back to `EDIPHOR_BLE.DEFAULT_CHUNK_SIZE`
   * only when the negotiated MTU cannot be determined.
   */
  private _computeChunkSize(server: BluetoothRemoteGATTServer): number {
    const mtu = this._getNegotiatedMtu(server);
    if (mtu === undefined) {
      return EDIPHOR_BLE.DEFAULT_CHUNK_SIZE;
    }
    return Math.min(
      EDIPHOR_BLE.MAX_CHUNK_SIZE,
      Math.max(EDIPHOR_BLE.DEFAULT_CHUNK_SIZE, mtu - EDIPHOR_BLE.ATT_HEADER_OVERHEAD)
    );
  }

  /**
   * Best-effort read of the connection's negotiated ATT MTU. The Web Bluetooth spec has
   * no standardized, universally-supported synchronous accessor for this value — probe
   * defensively for a non-standard extension some Chromium builds expose, and treat its
   * absence as the expected case (falls back to `EDIPHOR_BLE.DEFAULT_CHUNK_SIZE`).
   */
  private _getNegotiatedMtu(server: BluetoothRemoteGATTServer): number | undefined {
    const maybeMtu = (server as unknown as { mtu?: number }).mtu;
    return typeof maybeMtu === 'number' && maybeMtu > 0 ? maybeMtu : undefined;
  }

  /**
   * Throw CONNECTION_LOST if a `gattserverdisconnected` event (or an unrelated new
   * connect() call) has invalidated this connection attempt while it was mid-await —
   * `_initialiseOpenDevice` awaits several GATT calls in sequence, and without this guard
   * it would keep running against stale captured locals and could still mark the store
   * 'connected' / persist the paired-key after a real disconnect already fired.
   */
  private _assertStillConnecting(device: BluetoothDevice): void {
    if (this.device !== device || (this.server && !this.server.connected)) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST, 'Device disconnected during connection setup');
    }
  }

  /**
   * GATT-connect to an already-discovered device, subscribe to CHAR_READ/CHAR_ERROR,
   * and query initial device state. Shared shape with `DeviceService._initialiseOpenDevice`.
   *
   * Precondition: this.device has already been set to the target BluetoothDevice.
   */
  private async _initialiseOpenDevice(): Promise<DeviceInfo> {
    const device = this.device!;

    if (!device.gatt) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_FAILED, 'Device has no GATT server');
    }

    device.addEventListener('gattserverdisconnected', this.handleGattDisconnected);

    const server = await device.gatt.connect();
    this._assertStillConnecting(device);
    this.server = server;
    this.log('GATT connected');

    const service = await server.getPrimaryService(EDIPHOR_BLE.SERVICE_UUID);
    this._assertStillConnecting(device);

    const writeChar = await service.getCharacteristic(EDIPHOR_BLE.CHAR_WRITE_UUID);
    this._assertStillConnecting(device);
    const readChar = await service.getCharacteristic(EDIPHOR_BLE.CHAR_READ_UUID);
    this._assertStillConnecting(device);
    this.writeChar = writeChar;
    this.readChar = readChar;

    this._chunkSize = this._computeChunkSize(server);
    this.log('Negotiated write chunk size:', this._chunkSize);

    this._frameAssembler.reset(); // fresh connection: no stale partial frame
    readChar.addEventListener('characteristicvaluechanged', this._handleReadNotification);
    await readChar.startNotifications();
    this._assertStillConnecting(device);
    this.log('Subscribed to CHAR_READ notifications');

    // CHAR_ERROR subscription is best-effort — a failure here is non-fatal per
    // ble-gatt-contract.md ("Connection flow"). A staleness throw inside this try is still
    // caught here and swallowed as "non-fatal", but the unconditional check right after
    // the try/catch below still catches and bails out on a real disconnect.
    try {
      const errorChar = await service.getCharacteristic(EDIPHOR_BLE.CHAR_ERROR_UUID);
      this._assertStillConnecting(device);
      this.errorChar = errorChar;
      errorChar.addEventListener('characteristicvaluechanged', this._handleErrorNotification);
      await errorChar.startNotifications();
      this._assertStillConnecting(device);
      this.log('Subscribed to CHAR_ERROR notifications');
    } catch (e) {
      this.logger.warn('BleDeviceService', 'CHAR_ERROR subscription failed (non-fatal)', e);
      this.errorChar = null;
    }

    this._assertStillConnecting(device);

    let deviceInfo: DeviceInfo = {
      uid: UNKNOWN_DEVICE_UID,
      uidBytes: new Uint8Array(6),
      firmwareVersion: '0',
      hardwareVersion: '0',
      serialNumber: '0',
    };

    try {
      const devMsg = await this.sendCommand(Triggers.NONE);
      if (devMsg) {
        this.updateDeviceState(devMsg);
        const uid = Array.from(devMsg.uid ?? [])
          .map((b) => b.toString(16).padStart(2, '0'))
          .join(':');
        deviceInfo = {
          uid: devMsg.uid?.some((b) => b !== 0) ? uid : UNKNOWN_DEVICE_UID,
          uidBytes: devMsg.uid ?? new Uint8Array(6),
          firmwareVersion: getFirmwareVersion(devMsg.version),
          hardwareVersion: getHardwareVersion(devMsg.version),
          protocolVersion: getProtocolVersion(devMsg.version),
          serialNumber: devMsg.serialNumber?.toString() ?? '0',
        };
        this.logger.info('BleDeviceService', 'Post-connect query succeeded', deviceInfo);
      }
    } catch (e) {
      this.logger.warn('BleDeviceService', 'Post-connect state query failed (non-fatal)', e);
    }

    this._assertStillConnecting(device);

    const store = useDeviceStore.getState();
    store.setDeviceInfo(deviceInfo);
    store.setConnectionState('connected');
    store.clearError();
    localStorage.setItem(DEVICE_STORAGE.PAIRED_KEY, '1');

    return deviceInfo;
  }

  /**
   * Request user to select and connect to a device via the browser's Bluetooth picker.
   * Discovers by the confirmed GATT service UUID; no pairing/bonding step is attempted,
   * per the reference contract.
   */
  public async connect(): Promise<ConnectResult> {
    if (!this.isBleSupported()) {
      const error = createDeviceError(DeviceErrorCode.BLE_NOT_SUPPORTED);
      useDeviceStore.getState().setError(error);
      return { success: false, error: new Error(error.message) };
    }
    if (this._autoReconnectInProgress) {
      // A silent tryAutoReconnect() is already mid-flight — it sets this.device
      // synchronously before awaiting device.gatt.connect(), so a manual connect()
      // running concurrently would overwrite this.device/this.server out from under it.
      // Never let the two race each other.
      const error = createDeviceError(DeviceErrorCode.DEVICE_BUSY, 'Auto-reconnect already in progress');
      useDeviceStore.getState().setError(error);
      return { success: false, error: new Error(error.message) };
    }

    const store = useDeviceStore.getState();
    store.setConnectionState('connecting');

    try {
      const filter: BluetoothLEScanFilter = { services: [EDIPHOR_BLE.SERVICE_UUID] };
      if (this.options.serial !== undefined) {
        filter.name = `${EDIPHOR_BLE.DEVICE_NAME} ${this.options.serial}`;
      }

      this.device = await navigator.bluetooth!.requestDevice({ filters: [filter] });

      const deviceInfo = await this._initialiseOpenDevice();
      this._lastDevice = this.device;
      this.log('Connected to device');

      return {
        success: true,
        deviceInfo: { ...deviceInfo, serialNumber: parseInt(deviceInfo.serialNumber) || 0 },
      };
    } catch (error) {
      this.log('Connection failed:', error);
      // Preserve an already-classified DeviceError (e.g. CONNECTION_LOST from
      // _assertStillConnecting) instead of flattening it into a generic DEVICE_ERROR —
      // same shape check `sendCommand`/`runMeasurement` use.
      const isPlainDeviceError =
        typeof error === 'object' && error !== null && 'code' in error && 'message' in error && 'recoverable' in error;
      const deviceError = isPlainDeviceError ? (error as DeviceError) : classifyError(error);
      store.setConnectionState('disconnected');
      store.setError(deviceError);
      this.device = null;
      return { success: false, error: error as Error };
    }
  }

  /**
   * Disconnect from the current device.
   */
  public async disconnect(): Promise<void> {
    this._rejectAllPending(DeviceErrorCode.CONNECTION_LOST);

    // Discard any partially-reassembled message — never decode an incomplete buffer.
    this._frameAssembler.reset();

    const device = this.device;
    const server = this.server;
    const readChar = this.readChar;
    const errorChar = this.errorChar;
    this.device = null;
    this.server = null;
    this.writeChar = null;
    this.readChar = null;
    this.errorChar = null;
    // Explicit user-initiated disconnect ("forget this device") — unlike
    // handleGattDisconnected, this must clear _lastDevice so a later hook-driven
    // tryAutoReconnect() never silently reconnects after the user meant to disconnect.
    this._lastDevice = null;

    readChar?.removeEventListener('characteristicvaluechanged', this._handleReadNotification);
    errorChar?.removeEventListener('characteristicvaluechanged', this._handleErrorNotification);
    device?.removeEventListener('gattserverdisconnected', this.handleGattDisconnected);

    // Keyed off `server` itself (not `device`) so a still-live GATT connection is always
    // explicitly closed even if `this.device` was already nulled independently of
    // `this.server` (e.g. a failed tryAutoReconnect() that got partway through connecting).
    if (server?.connected) {
      try {
        server.disconnect();
      } catch {
        // Ignore errors during disconnect
      }
    }

    const store = useDeviceStore.getState();
    localStorage.removeItem(DEVICE_STORAGE.PAIRED_KEY);
    store.reset();
    this.log('Disconnected');
  }

  /**
   * Trigger device LED blink for identification
   */
  public async blink(): Promise<DevMessage | null> {
    try {
      return await this.sendCommand(Triggers.BLINK);
    } catch {
      return null;
    }
  }

  /**
   * Get current device state
   */
  public async getState(): Promise<DevMessage | null> {
    return this.sendCommand(Triggers.NONE);
  }

  /**
   * Send a custom command (for diagnostic/testing purposes)
   */
  public async sendCustomCommand(
    trigger: Triggers,
    impedanceExec?: ImpedanceExec
  ): Promise<DevMessage | null> {
    return this.sendCommand(trigger, impedanceExec);
  }

  /**
   * Subscribe to unsolicited (watchdog) messages from the device.
   * Returns an unsubscribe function.
   */
  public onUnsolicitedMessage(
    handler: (msg: DevMessage, raw: Uint8Array) => void
  ): () => void {
    this._unsolicitedHandlers.push(handler);
    return () => {
      this._unsolicitedHandlers = this._unsolicitedHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Subscribe to raw frames transmitted to the device (harness communication logging).
   * Returns an unsubscribe function.
   */
  public onSentMessage(handler: (raw: Uint8Array, message: AppMessage) => void): () => void {
    this._sentHandlers.push(handler);
    return () => {
      this._sentHandlers = this._sentHandlers.filter((h) => h !== handler);
    };
  }

  private notifySent(raw: Uint8Array, message: AppMessage): void {
    for (const handler of this._sentHandlers) {
      try {
        handler(raw, message);
      } catch (e) {
        this.logger.warn('BLE TX', 'Sent-message handler threw', e);
      }
    }
  }

  /**
   * Subscribe to EVERY raw frame received from the device (harness communication logging).
   * Superset of `onUnsolicitedMessage`. Returns an unsubscribe function.
   */
  public onReceivedMessage(
    handler: (raw: Uint8Array, msg: DevMessage, crcValid: boolean) => void
  ): () => void {
    this._receivedHandlers.push(handler);
    return () => {
      this._receivedHandlers = this._receivedHandlers.filter((h) => h !== handler);
    };
  }

  private notifyReceived(raw: Uint8Array, msg: DevMessage, crcValid = true): void {
    for (const handler of this._receivedHandlers) {
      try {
        handler(raw, msg, crcValid);
      } catch (e) {
        this.logger.warn('BLE RX', 'Received-message handler threw', e);
      }
    }
  }

  /**
   * Subscribe to write-chunk failure incidents. Returns an unsubscribe function.
   */
  public onTransferIncident(handler: (incident: TransferIncident) => void): () => void {
    this._transferIncidentHandlers.push(handler);
    return () => {
      this._transferIncidentHandlers = this._transferIncidentHandlers.filter((h) => h !== handler);
    };
  }

  private notifyTransferIncident(incident: TransferIncident): void {
    for (const handler of this._transferIncidentHandlers) {
      try {
        handler(incident);
      } catch (e) {
        this.logger.warn('BLE RX', 'Transfer-incident handler threw', e);
      }
    }
  }

  /** Count of inbound frames whose CRC32 failed but were decoded and used anyway (diagnostics). */
  public get crcFallbackUsedCount(): number {
    return this._crcFallbackUsedCount;
  }

  /**
   * Return the raw wire bytes of the most recent command response (diagnostic parity
   * with `DeviceService.getLastRawResponse()` — not part of `IDeviceService`). Returns
   * `null` if no command has completed yet, or the last command ended in an error.
   */
  public getLastRawResponse(): Uint8Array | null {
    return this._lastCmdRaw;
  }

  /**
   * Resend the last response from the device.
   */
  public async resend(): Promise<DevMessage | null> {
    return this.sendCommand(Triggers.MEASURE_RESULT);
  }

  /**
   * Attempt to reconnect to a previously-connected device without user interaction.
   *
   * Same-tab, same-session retry against the already-held `BluetoothDevice` object
   * (`_lastDevice`) — never calls `navigator.bluetooth.requestDevice()`/`getDevices()`,
   * so it never prompts. Mirrors `DeviceService.tryAutoReconnect()`'s guard/shape:
   * a top-of-method `_autoReconnectInProgress` check and a `finally` reset.
   *
   * Returns `false` (without attempting anything) when there is no retained device —
   * either nothing has ever connected, or a prior explicit `disconnect()` cleared
   * `_lastDevice` — and after a page reload, since a freshly-constructed singleton's
   * `_lastDevice` is always `null`.
   */
  public async tryAutoReconnect(): Promise<boolean> {
    logger.debug('[RECOVERY:BleDevice] tryAutoReconnect() called');
    if (this.device) {
      logger.debug('[RECOVERY:BleDevice] Already connected — skipping auto-reconnect');
      return false;
    }
    if (this._autoReconnectInProgress) {
      logger.debug('[RECOVERY:BleDevice] tryAutoReconnect: already in progress — skipping concurrent call');
      return false;
    }
    if (!this._lastDevice) {
      logger.debug('[RECOVERY:BleDevice] No previously-connected device — cannot auto-reconnect');
      return false;
    }
    this._autoReconnectInProgress = true;

    try {
      this.device = this._lastDevice;
      await this._initialiseOpenDevice();
      this._lastDevice = this.device;

      logger.debug('[RECOVERY:BleDevice] tryAutoReconnect: SUCCESS ✓');
      return true;
    } catch (error) {
      logger.warn('[RECOVERY:BleDevice] tryAutoReconnect: FAILED ✗', error);
      // _initialiseOpenDevice() may have gotten partway through — GATT connect succeeded
      // (this.server assigned) before a later step (getPrimaryService/getCharacteristic/…)
      // threw. That connection is still live; explicitly close it (same try/catch-ignore
      // shape disconnect() itself uses) before discarding the only reference to it.
      if (this.server?.connected) {
        try {
          this.server.disconnect();
        } catch {
          // Ignore errors during disconnect
        }
      }
      // Full teardown, mirroring handleGattDisconnected, so a later disconnect() never
      // sees a mismatched device/server pair to clean up.
      this.device = null;
      this.server = null;
      this.writeChar = null;
      this.readChar = null;
      this.errorChar = null;
      return false;
    } finally {
      this._autoReconnectInProgress = false;
    }
  }

  /**
   * Subscribe to an already-running measurement's push frames without calling
   * runMeasurement(). Used for recovery when the device is still mid-measurement.
   */
  public async subscribeMeasurement(options: {
    onProgress?: (progress: number) => void;
    onStateUpdate?: (state: DeviceState) => void;
    abortSignal?: AbortSignal;
  }): Promise<DevMessage> {
    if (!this.device) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    return this.awaitMeasurementCompletion({
      onProgress: options.onProgress,
      abortSignal: options.abortSignal,
      onStateUpdate: options.onStateUpdate
        ? (_busy: boolean, progress: number) => {
            const store = useDeviceStore.getState();
            options.onStateUpdate!(store.deviceState);
            return progress;
          }
        : undefined,
    });
  }

  /**
   * Run a measurement with the given profile
   */
  public async runMeasurement(options: MeasurementOptions): Promise<MeasurementResult> {
    const store = useDeviceStore.getState();

    if (!this.device) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    if (store.deviceState.busy) {
      throw createDeviceError(DeviceErrorCode.DEVICE_BUSY);
    }

    if (!store.deviceState.electrodeDetected) {
      throw createDeviceError(DeviceErrorCode.ELECTRODE_NOT_DETECTED);
    }

    const startTime = Date.now();
    store.setConnectionState('busy');
    store.updateMeasurementProgress({
      isRunning: true,
      progress: 0,
      startedAt: new Date(),
    });

    try {
      if (!options.impedanceExec) {
        throw createDeviceError(
          DeviceErrorCode.INVALID_PROFILE,
          'Measurement profile is required to start a measurement'
        );
      }

      const { channels, biasMV, amplitudeMV, frequencyHz, intervalMs } = options.impedanceExec;
      const computedPointCount =
        channels.length *
        biasMV.length *
        amplitudeMV.length *
        frequencyHz.length *
        Math.max(1, intervalMs.length);

      if (
        store.deviceState.pointsMax !== null &&
        store.deviceState.pointsMax > 0 &&
        computedPointCount > store.deviceState.pointsMax
      ) {
        throw createDeviceError(
          DeviceErrorCode.POINTS_LIMIT_EXCEEDED,
          `Measurement requires ${computedPointCount} points, exceeding the device's limit of ${store.deviceState.pointsMax}`
        );
      }

      // MEASURE_START is correlated through the same resolver queue as every other
      // trigger (AD-3): it settles on either an ACK echo or the first frame whose
      // state.processingId equals the minted id. Settling is additive — that frame is
      // still forwarded to the watchdog handlers. Subscribe to completion FIRST so the
      // settling frame is never missed.
      const completion = this.beginMeasurementCompletion(options);
      let result: DevMessage;
      try {
        const start = this.sendCommand(
          Triggers.MEASURE_START,
          options.impedanceExec,
          TIMING.MEASUREMENT_STALE_TIMEOUT
        ).then(() => undefined);
        // If completion wins the race (abort, or it finished off the settling frame),
        // a later start failure has no one left to report to.
        start.catch(() => {});
        await Promise.race([start, completion.promise]);
        result = await completion.promise;
      } catch (error) {
        completion.cancel();
        completion.promise.catch(() => {});
        if (
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          (error as { code: unknown }).code === DeviceErrorCode.RECEIVE_TIMEOUT
        ) {
          throw createDeviceError(
            DeviceErrorCode.MEASUREMENT_TIMEOUT,
            'No response from device for ' + TIMING.MEASUREMENT_STALE_TIMEOUT + ' ms — device may have stalled'
          );
        }
        throw error;
      } finally {
        // Whatever won the race, no MEASURE_START resolver may outlive this call.
        for (const [id, trig] of [...this._pendingTriggerByCorrelationId]) {
          if (trig === Triggers.MEASURE_START) this._cancelResolver(id);
        }
      }

      const measurementResult: MeasurementResult = {
        deviceUid: store.deviceInfo!.uid,
        deviceSerialNumber: store.deviceInfo!.serialNumber,
        firmwareVersion: store.deviceInfo!.firmwareVersion,
        hardwareVersion: store.deviceInfo!.hardwareVersion,
        durationMs: Date.now() - startTime,
        impedanceResult: result.impedanceResult!,
        errorCode: result.errorCode,
        completedAt: new Date(),
      };

      store.setMeasurementResult(measurementResult);
      store.setConnectionState('connected');

      return measurementResult;
    } catch (error) {
      const liveState = useDeviceStore.getState().connectionState;
      if (liveState !== 'disconnected' && liveState !== 'error') {
        store.setConnectionState('connected');
      }
      store.updateMeasurementProgress({ isRunning: false, progress: 0 });

      const isPlainDeviceError =
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        'message' in error &&
        'recoverable' in error;
      if (isPlainDeviceError) {
        throw error;
      }
      throw classifyError(error);
    }
  }

  /**
   * Cancel the current measurement
   */
  public async cancelMeasurement(): Promise<void> {
    try {
      await this.sendCommand(Triggers.MEASURE_ABORT);
    } catch {
      // Ignore cancel errors
    }

    const store = useDeviceStore.getState();
    store.updateMeasurementProgress({ isRunning: false, progress: 0 });
    store.setConnectionState('connected');
  }

  // ========== Private Methods ==========

  /**
   * Reject every pending command resolver and clear correlation bookkeeping — shared
   * by disconnect() and the GATT-disconnected event handler.
   */
  private _rejectAllPending(code: DeviceErrorCode): void {
    for (const entry of this._rxResolvers) {
      clearTimeout(entry.timer);
      entry.reject(createDeviceError(code));
    }
    this._rxResolvers = [];
    this._pendingTriggerByCorrelationId.clear();
  }

  /**
   * Split an encoded frame into MTU-sized chunks and write each sequentially to
   * CHAR_WRITE with write-with-response, per the reference contract. A failed write
   * anywhere in the sequence reports a TransferIncident (mirroring the classification
   * USB's transferOut failure uses) and aborts the remaining chunks.
   */
  private async _writeChunked(data: Uint8Array): Promise<void> {
    const writeChar = this.writeChar;
    if (!writeChar) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    const chunkSize = Math.max(1, this._chunkSize);
    const totalChunks = Math.max(1, Math.ceil(data.length / chunkSize));
    const startedAt = Date.now();

    for (let chunkIndex = 0, offset = 0; chunkIndex < totalChunks; chunkIndex++, offset += chunkSize) {
      const chunk = data.slice(offset, offset + chunkSize);
      try {
        await writeChar.writeValueWithResponse(chunk);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.logger.warn(
          'BLE TX',
          `writeValueWithResponse failed on chunk ${chunkIndex + 1}/${totalChunks}`,
          error
        );
        this.notifyTransferIncident({
          phase: 'failed',
          consecutiveErrors: chunkIndex + 1,
          durationMs: Date.now() - startedAt,
          errorMessage,
        });
        throw createDeviceError(DeviceErrorCode.SEND_FAILED);
      }
    }
  }

  /**
   * Send a command to the device and wait for response
   */
  private async sendCommand(
    trigger: Triggers,
    impedanceExec?: ImpedanceExec,
    responseTimeoutMs?: number
  ): Promise<DevMessage | null> {
    if (!this.writeChar) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    const message = createAppMessage(trigger, impedanceExec);
    const correlationId = Number(message.id);
    this._pendingTriggerByCorrelationId.set(correlationId, trigger);
    const encoded = encodeAppMessage(message);

    const triggerName = Triggers[trigger] ?? `UNKNOWN(${trigger})`;
    this._lastCmdRaw = null;
    this.logger.trace('BLE TX', `-> [${triggerName}] ${encoded.length} bytes`, {
      triggerName,
      byteLength: encoded.length,
      message,
    });

    // Register the resolver BEFORE the write so a reply that lands while the write is
    // still settling is never dropped as "no resolver waiting".
    const responsePromise = this.receiveResponse(correlationId, responseTimeoutMs);
    // If the send fails we abandon this promise; if the resolver rejects first (e.g.
    // disconnect) before we await it, don't surface that as an unhandled rejection.
    responsePromise.catch(() => {});

    try {
      await this._writeChunked(encoded);
      this.notifySent(encoded, message);

      const response = await responsePromise;

      if (response) {
        const isValid = validateAcknowledgement(message, response);
        if (!isValid) {
          this.logger.error('BLE RX', 'Acknowledgement mismatch', { sent: trigger, received: response });
          throw createDeviceError(
            DeviceErrorCode.ACKNOWLEDGEMENT_MISMATCH,
            'Device did not correctly acknowledge command - possible communication corruption'
          );
        }
        this.logger.trace('BLE TX', `ACK OK for ${triggerName}`);
      }

      return response;
    } catch (error) {
      this._cancelResolver(correlationId); // no-op if already settled/timed out
      this.logger.error('BLE TX', `Command failed: ${triggerName}`, error);
      if (error && typeof error === 'object' && 'code' in error) {
        throw error;
      }
      throw classifyError(error);
    }
  }

  /**
   * Wait for a command response from the device that matches the given message ID.
   */
  private receiveResponse(expectedId: number, timeoutMs?: number): Promise<DevMessage | null> {
    if (!this.device) {
      return Promise.resolve(null);
    }

    return new Promise<DevMessage | null>((resolve, reject) => {
      const entry: {
        resolve: (msg: DevMessage | null) => void;
        reject: (err: unknown) => void;
        timer: ReturnType<typeof setTimeout>;
        expectedId: number;
      } = {
        resolve,
        reject,
        expectedId,
        timer: setTimeout(() => {
          const idx = this._rxResolvers.indexOf(entry);
          if (idx >= 0) this._rxResolvers.splice(idx, 1);
          this._pendingTriggerByCorrelationId.delete(expectedId);
          this.logger.warn('BLE RX', `receiveResponse: timeout waiting for id=${expectedId} — no matching response from device`);
          reject(createDeviceError(DeviceErrorCode.RECEIVE_TIMEOUT));
        }, timeoutMs ?? this.options.timeouts.command),
      };

      this._rxResolvers.push(entry);
    });
  }

  /** Drop a pending resolver without settling it (send failed, so no reply can come). */
  private _cancelResolver(expectedId: number): void {
    const idx = this._rxResolvers.findIndex((e) => e.expectedId === expectedId);
    if (idx >= 0) {
      clearTimeout(this._rxResolvers[idx].timer);
      this._rxResolvers.splice(idx, 1);
    }
    this._pendingTriggerByCorrelationId.delete(expectedId);
  }

  // ========== Notification handling / frame reassembly ==========

  /**
   * `characteristicvaluechanged` listener for CHAR_READ. Appends the new chunk to the
   * reassembly buffer and extracts as many complete 0xAA55-framed messages as are
   * available — a `DevMessage` may arrive split across multiple notifications when it
   * exceeds one MTU-sized packet.
   */
  private _handleReadNotification = (event: Event): void => {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    const value = characteristic.value;
    if (!value) return;

    const chunk = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    // Every complete payload is re-framed so downstream code keeps seeing full-frame `raw` bytes.
    for (const payload of this._frameAssembler.feed(chunk)) {
      this._processFrame(frameBytes(payload));
    }
  };

  /**
   * `characteristicvaluechanged` listener for CHAR_ERROR. The device pushes a UTF-8
   * error string, decoupled from the main response channel — surfaced through the same
   * error-reporting path `DeviceService` already exposes (`store.setError`).
   */
  private _handleErrorNotification = (event: Event): void => {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    const value = characteristic.value;
    if (!value) return;

    const bytes = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    const errorText = new TextDecoder('utf-8').decode(bytes);
    this.logger.warn('BLE ERROR', `Device error notification: ${errorText}`);

    useDeviceStore.getState().setError(createDeviceError(DeviceErrorCode.DEVICE_ERROR, errorText));
  };

  /**
   * Handle one complete, framed inbound frame (`raw` = header + protobuf + CRC32).
   * Decodes it and routes it to a command resolver or the watchdog path.
   */
  private _processFrame(raw: Uint8Array): void {
    const decodeResult = tryDecodeDevMessageWithCrcStatus(raw);
    if (!decodeResult) {
      this.logger.warn('BLE RX', `Failed to decode ${raw.length}-byte frame`);
      return;
    }

    // The single errorCode check for frames that do NOT settle a resolver: anything that
    // settles nothing (e.g. a refusal arriving after MEASURE_START already settled) is
    // logged only, never re-settled.
    const settledResolver = this._handleDecodedFrame(raw, decodeResult);
    const devMessage = decodeResult.message;
    // A CRC-failed frame that was discarded never reached routing — nothing more to report.
    const discarded = !decodeResult.crcValid && !CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH;
    if (!settledResolver && !discarded && hasError(devMessage)) {
      this.logger.warn(
        'BLE RX',
        `Device error code: ${devMessage.errorCode} on a frame that settles no pending command (${getErrorMessage(devMessage.errorCode)})`,
        devMessage
      );
    }
  }

  /**
   * Route a fully-decoded frame to its matching command resolver, or to the
   * unsolicited/watchdog handlers when no command is in flight. Mirrors
   * `DeviceService._receiveLoop`'s per-message logic (duplicated per the story's
   * Design Notes rather than extracted into a shared module).
   */
  private _handleDecodedFrame(raw: Uint8Array, decodeResult: DevMessageDecodeResult): boolean {
    const hexDump = Array.from(raw)
      .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');

    if (!decodeResult.crcValid) {
      this._crcFallbackUsedCount++;
      this.logger.warn(
        'BLE RX',
        `CRC32 mismatch on ${raw.length}-byte frame (received 0x${decodeResult.receivedCrc
          .toString(16)
          .padStart(8, '0')}, calculated 0x${decodeResult.calculatedCrc
          .toString(16)
          .padStart(8, '0')}) — ` +
          `${CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH ? `decoding anyway (fallback use #${this._crcFallbackUsedCount})` : 'discarding'}`,
        { hexDump, message: decodeResult.message }
      );

      this.notifyReceived(raw, decodeResult.message, false);

      if (!CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH) {
        return false;
      }
    } else {
      this.notifyReceived(raw, decodeResult.message, true);
    }

    const devMessage = decodeResult.message;

    if (this._rxResolvers.length === 0) {
      this.handleWatchdogMessage(devMessage, raw, hexDump);
      return false;
    }

    this.updateDeviceState(devMessage);

    const processingId = Number(devMessage.state?.processingId ?? 0n);
    const correlationId =
      devMessage.appMessage?.id !== undefined
        ? Number(devMessage.appMessage.id)
        : processingId !== 0
          ? processingId
          : undefined;

    if (!correlationId) {
      this.handleWatchdogMessage(devMessage, raw, hexDump);
      return false;
    }

    const matchIdx = this._rxResolvers.findIndex((e) => e.expectedId === correlationId);

    if (matchIdx === -1) {
      if (!devMessage.appMessage?.id && processingId !== 0) {
        this.handleWatchdogMessage(devMessage, raw, hexDump);
        return false;
      }
      this.logger.warn(
        'BLE RX',
        `Unexpected response correlationId=${correlationId} — no resolver waiting, discarding`,
        { byteLength: raw.length, hexDump, message: devMessage }
      );
      return false;
    }

    const [entry] = this._rxResolvers.splice(matchIdx, 1);
    clearTimeout(entry.timer);

    const sentTrigger = this._pendingTriggerByCorrelationId.get(entry.expectedId);
    this._pendingTriggerByCorrelationId.delete(entry.expectedId);

    if (hasError(devMessage)) {
      // MEASURE_START joins the set: a recoverable code on it resolves (not rejects),
      // per AD-3, so the sweep is not failed on a code that still carries valid data.
      const canCarryPartialResult =
        sentTrigger === Triggers.MEASURE_ABORT ||
        sentTrigger === Triggers.MEASURE_RESULT ||
        sentTrigger === Triggers.MEASURE_START;
      if (isRecoverableError(devMessage.errorCode) && canCarryPartialResult) {
        this._lastCmdRaw = raw;
        entry.resolve(devMessage);
      } else {
        entry.reject(
          createDeviceError(
            DeviceErrorCode.DEVICE_ERROR,
            getErrorMessage(devMessage.errorCode),
            true,
            undefined,
            devMessage.errorCode
          )
        );
      }
    } else {
      this._lastCmdRaw = raw;
      entry.resolve(devMessage);
    }

    // AD-3: settling MEASURE_START's resolver is additive bookkeeping, never exclusive
    // routing — the settling frame still goes to the watchdog handlers so
    // awaitMeasurementCompletion never misses it.
    if (sentTrigger === Triggers.MEASURE_START) {
      this.handleWatchdogMessage(devMessage, raw, hexDump);
    }
    return true;
  }

  /**
   * Internal helper that handles an incoming unsolicited / watchdog frame.
   */
  private handleWatchdogMessage(devMessage: DevMessage, raw: Uint8Array, hexDump?: string): void {
    const dump =
      hexDump ??
      Array.from(raw)
        .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
        .join(' ');
    this.logger.trace('BLE RX (WD)', `<- WATCHDOG ${raw.length} bytes`, {
      byteLength: raw.length,
      hexDump: dump,
      message: devMessage,
    });

    this.updateDeviceState(devMessage);

    for (const handler of this._unsolicitedHandlers) {
      try {
        handler(devMessage, raw);
      } catch (e) {
        this.logger.warn('BLE RX (WD)', 'Unsolicited handler threw', e);
      }
    }
  }

  /**
   * GATT disconnect callback — signals connection loss the same way USB's
   * `handleDisconnect` does. Full silent-reconnect UX is story 3 (CAP-4); for this
   * foundational story this just tears down state and surfaces the error.
   */
  private handleGattDisconnected = (): void => {
    this.log('GATT server disconnected');
    this.device = null;
    this.server = null;
    this.writeChar = null;
    this.readChar = null;
    this.errorChar = null;
    this._frameAssembler.reset();

    this._rejectAllPending(DeviceErrorCode.CONNECTION_LOST);

    const store = useDeviceStore.getState();
    store.setConnectionState('disconnected');
    store.setError(createDeviceError(DeviceErrorCode.CONNECTION_LOST));
  };

  /**
   * Wait for measurement completion by listening to unsolicited push messages from the
   * device. Same shape as `DeviceService.awaitMeasurementCompletion` (duplicated per the
   * story's Design Notes).
   */
  private awaitMeasurementCompletion(options: MeasurementOptions): Promise<DevMessage> {
    return this.beginMeasurementCompletion(options).promise;
  }

  /**
   * Same as {@link awaitMeasurementCompletion}, but also returns a `cancel()` handle that
   * tears down the listener, timers and abort hook without settling the promise. The
   * subscription is live as soon as this returns, so callers can subscribe *before*
   * sending MEASURE_START and never miss the frame that settles its correlation.
   */
  private beginMeasurementCompletion(
    options: MeasurementOptions
  ): { promise: Promise<DevMessage>; cancel: () => void } {
    const store = useDeviceStore.getState();
    const startTime = Date.now();
    let cancel: () => void = () => {};

    const promise = new Promise<DevMessage>((resolve, reject) => {
      let staleTimer: ReturnType<typeof setTimeout> | null = null;
      let maxedProgress = false;
      let lastProcessingId = 0;
      let lastImpedanceResult: ImpedanceResult | undefined = undefined;

      const onAbort = () => {
        cleanup();
        reject(createDeviceError(DeviceErrorCode.MEASUREMENT_CANCELLED));
      };
      options.abortSignal?.addEventListener('abort', onAbort);

      const maxTimer = setTimeout(() => {
        cleanup();
        reject(createDeviceError(
          DeviceErrorCode.MEASUREMENT_TIMEOUT,
          'Measurement exceeded maximum allowed duration'
        ));
      }, this.options.timeouts.measurement);

      const resetStaleTimer = () => {
        if (staleTimer) clearTimeout(staleTimer);
        staleTimer = setTimeout(() => {
          cleanup();
          reject(createDeviceError(
            DeviceErrorCode.MEASUREMENT_TIMEOUT,
            'No response from device for ' + TIMING.MEASUREMENT_STALE_TIMEOUT + ' ms — device may have stalled'
          ));
        }, TIMING.MEASUREMENT_STALE_TIMEOUT);
      };

      const cleanup = () => {
        if (staleTimer) clearTimeout(staleTimer);
        clearTimeout(maxTimer);
        options.abortSignal?.removeEventListener('abort', onAbort);
        unsubscribe();
      };

      cancel = cleanup;

      const unsubscribe = this.onUnsolicitedMessage((msg) => {
        resetStaleTimer();

        const progress = msg.state?.progressPct ?? 0;
        const currentProcessingId = Number(msg.state?.processingId ?? 0n);

        store.updateMeasurementProgress({
          progress,
          estimatedTimeRemaining: this.estimateTimeRemaining(progress, startTime),
        });
        options.onProgress?.(progress);
        options.onStateUpdate?.(currentProcessingId !== 0, progress);

        if (progress >= 100) {
          maxedProgress = true;
        }

        if (msg.impedanceResult) {
          lastImpedanceResult = msg.impedanceResult;
        }

        const wasProcessing = lastProcessingId !== 0;
        const isNowIdle = currentProcessingId === 0;
        const transitioned = wasProcessing && isNowIdle;

        if ((transitioned && maxedProgress) || msg.impedanceResult) {
          cleanup();
          resolve({ ...msg, impedanceResult: msg.impedanceResult ?? lastImpedanceResult });
        }

        lastProcessingId = currentProcessingId;
      });

      resetStaleTimer();

      this.logger.info('BleDeviceService', 'awaitMeasurementCompletion: listening for push updates from device');
    });

    return { promise, cancel: () => cancel() };
  }

  /**
   * Update device state in store from DevMessage. Same shape as
   * `DeviceService.updateDeviceState` (duplicated per the story's Design Notes).
   */
  private updateDeviceState(devMessage: DevMessage): void {
    if (devMessage.limits) {
      useDeviceStore.getState().updateDeviceState({
        pointsMax: devMessage.limits.pointsMax,
      });
    }

    if (!devMessage.state) return;

    const store = useDeviceStore.getState();
    const state = devMessage.state;

    const electrodeType = (): DeviceState['electrodeType'] => {
      // See DeviceService.updateDeviceState for rationale — no known physical
      // electrode mapping yet for CODE_1-CODE_7.
      return 'unknown';
    };

    const powerState = (): DeviceState['powerState'] => {
      switch (state.powerState) {
        case PowerState.BATTERY:
          return 'battery';
        case PowerState.CHARGING:
          return 'charging';
        case PowerState.STANDBY:
          return 'standby';
        case PowerState.FAULT:
          return 'fault';
        case PowerState.NO_BATTERY:
          return 'no_battery';
        default:
          return 'unknown';
      }
    };

    store.updateDeviceState({
      busy: state.processingId !== 0n,
      progress: state.progressPct,
      electrodeDetected: state.electrode !== ElectrodeDetect.NC,
      electrodeType: electrodeType(),
      temperature: state.temperatureC,
      humidity: state.humidityPct,
      batteryLevel: state.batteryPct,
      batteryVoltage: state.batteryV,
      powerState: powerState(),
      warnings: Number(state.issueBitmask),
    });
  }

  /**
   * Estimate remaining time based on progress
   */
  private estimateTimeRemaining(progress: number, startTime: number): number {
    if (progress <= 0) return 0;
    const elapsed = Date.now() - startTime;
    const total = elapsed / (progress / 100);
    return Math.max(0, total - elapsed);
  }

  /**
   * Debug logging
   */
  private log(...args: unknown[]): void {
    const message = args
      .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
      .join(' ');
    logger.debug('[BleDeviceService]', message);
  }
}

// Default export for convenience
export const bleDeviceService = BleDeviceService.getInstance();
